// src/index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import App from './App';
import keycloak from '@/lib/keycloak';
import { syncKeycloakToLocalStorage } from '@/utils/keycloakSync';
import { consumeDeepLink, stashDeepLink } from '@/utils/deepLink';
import { cleanDevelopersHash } from '@/features/developers/tabs';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com';
const PORTAL_LOGIN_URL = `${PORTAL_URL}?app=insight`;

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// ── Public shareable panel short-circuit ──────────────────────────────────────
// A panel link (#/panel/<token>) must render WITHOUT any session: it is viewed on
// a wall display / phone with no login. Detect it before the Keycloak bootstrap so
// the visitor never enters the check-sso / portal redirect flow.
const PANEL_HASH = /^#\/panel\/([^/?#]+)/;
const panelMatch = window.location.hash.match(PANEL_HASH);
if (panelMatch) {
  const token = decodeURIComponent(panelMatch[1]);
  import('@/panel/PanelApp').then(({ PanelApp }) => {
    root.render(
      <React.StrictMode>
        <PanelApp token={token} />
      </React.StrictMode>
    );
  });
} else {
  bootstrapApp();
}

function bootstrapApp() {

// The portal redirects here with Keycloak callback params (?code=...&state=...)
// that belong to the portal's client, not to "insight".  keycloak-js would try
// to exchange that code and fail with 401.
// We use a sessionStorage flag to tell apart the portal's params (strip)
// from our own keycloak-js callback (keep).
const KC_REDIRECT_FLAG = 'kc_insight_redirect';
const url = new URL(window.location.href);
const hasCode = url.searchParams.has('code');
const hasFlag = !!sessionStorage.getItem(KC_REDIRECT_FLAG);

if (hasCode && !hasFlag) {
  // Code comes from the portal — strip it before keycloak-js sees it
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('session_state');
  url.searchParams.delete('iss');
  window.history.replaceState({}, '', url.toString());
}

// A reload or a pasted link (#/live-board) goes through the sign-in redirect,
// whose return address cannot carry the in-app route: keep it for the return.
// The reference viewer appends its own route (with braces) to the Developers address: cut it first.
const cleanedHash = cleanDevelopersHash(window.location.hash);
if (cleanedHash) {
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${cleanedHash}`);
}
const inAppRoute = window.location.hash.replace(/^#/, '');
if (!hasCode && inAppRoute && inAppRoute !== '/') {
  stashDeepLink(inAppRoute);
}

// Set flag before init — if keycloak-js redirects to Keycloak,
// the flag will survive in sessionStorage for the callback page load.
sessionStorage.setItem(KC_REDIRECT_FLAG, '1');

// check-sso (full redirect, no iframe) verifies the Keycloak session:
//  - Session exists  → redirects back with code → authenticated = true
//  - No session      → redirects back with error → authenticated = false → portal
// checkLoginIframe disabled to avoid third-party cookie issues.
keycloak
  .init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    responseMode: 'query',
    checkLoginIframe: false,
    redirectUri: window.location.origin + '/',
  })
  .then((authenticated) => {
    sessionStorage.removeItem(KC_REDIRECT_FLAG);

    if (!authenticated) {
      window.location.href = PORTAL_LOGIN_URL;
      return;
    }

    // Sync Keycloak token data to localStorage for backward compatibility
    syncKeycloakToLocalStorage(keycloak);

    // Back from the sign-in redirect: land on the route the user asked for
    // (this page after a reload, or the one another tab handed over) before
    // the router starts, so the start page never shows in between.
    const pendingRoute = consumeDeepLink();
    if (pendingRoute) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${pendingRoute}`);
    }

    // Automatic silent token refresh
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        window.location.href = PORTAL_LOGIN_URL;
      });
    };

    // Re-sync localStorage after a successful silent refresh
    keycloak.onAuthRefreshSuccess = () => {
      syncKeycloakToLocalStorage(keycloak);
    };

    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  })
  .catch((err) => {
    sessionStorage.removeItem(KC_REDIRECT_FLAG);
    console.error('Keycloak init failed:', err);
    window.location.href = PORTAL_LOGIN_URL;
  });

}
